import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Checkbox } from '@/components/ui/checkbox'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Slider } from '@/components/ui/slider'
import { Upload, Target, TrendingUp, Eye, Hotel, Utensils, Plane } from 'lucide-react'
import  HeroSection from '@/components/marketing/HeroSection'
import  FeaturesGrid from '@/components/marketing/FeaturesGrid'
import  LocalPresenceSection from '@/components/marketing/LocalPresenceSection'
import  CTABanner from '@/components/marketing/CTABanner'

const heroImg = '/3.png'
const featuresImg = '/4.png'
const ctaImg = '/8.png'

const citiesList = ['العيون', 'السمارة', 'بوجدور', 'الطرفاية', 'المرسى']
const neighborhoodsList = ['شارع سكيكيمة', 'حي الولاية', 'كولومينا', 'الحشيشة', 'النهضة', 'شارع بوكراع', 'حي الانعاش', 'المسيرة']

export const Route = createFileRoute('/_regular/post-your-ad/')({
  component: PostYourAdPage,
  head: () => ({
    meta: [
      { title: 'ضع إعلانك | سكيكيمة' },
      {
        name: 'description',
        content: 'اعرض إعلانك على سكيكيمة وصل إلى عملاء جدد في الصحراء.',
      },
    ],
  }),
})

function PostYourAdPage() {
  const navigate = useNavigate()
  const { isAuthenticated } = useAuth()
  const [adTitle, setAdTitle] = useState('')
  const [ageRange, setAgeRange] = useState([18, 45])
  const [selectedCities, setSelectedCities] = useState([])
  const [selectedNeighborhoods, setSelectedNeighborhoods] = useState([])
  const [interests, setInterests] = useState([])
  const [gender, setGender] = useState('all')

  const handlePostAd = () => {
    if (isAuthenticated) {
      navigate({ to: '/dashboard/create-ad' })
    } else {
      navigate({ to: '/login', search: { redirect: '/dashboard/create-ad' } })
    }
  }

  const handleCityToggle = (city) => {
    setSelectedCities(prev =>
      prev.includes(city) ? prev.filter(c => c !== city) : [...prev, city]
    )
  }

  const handleNeighborhoodToggle = (hood) => {
    setSelectedNeighborhoods(prev =>
      prev.includes(hood) ? prev.filter(h => h !== hood) : [...prev, hood]
    )
  }

  const handleInterestToggle = (interest) => {
    setInterests(prev =>
      prev.includes(interest) ? prev.filter(i => i !== interest) : [...prev, interest]
    )
  }

  return (
    <div className="min-h-screen bg-background" dir="rtl">
      <HeroSection
  title={
    <>
      أعلن عن <bdi className="text-primary">منتجك أو خدمتك</bdi> – واصل إلى آلاف العملاء
    </>
  }
  subtitle="سواء كنت صاحب متجر، فندق، مطعم، وكالة سفر، أو تقدم خدمة محلية – سكيكيمة تمنحك منصة إعلانية قوية لاستهداف الجمهور في الصحراء."
  buttonText="أعلن الآن – ابدأ ب 25 درهماً"
  onButtonClick={handlePostAd}
  imageSrc={heroImg}
  imageAlt="إعلانات سكيكيمة"
/>

      <FeaturesGrid
        title="أعلن لأي نشاط تجاري"
        subtitle="منصة مفتوحة لجميع أنواع الخدمات والمنتجات – نأخذ إعلانك إلى الزبائن المناسبين."
        features={[
          { icon: Target, title: 'استهداف دقيق', desc: 'اختر المدينة، الحي، الفئة العمرية' },
          { icon: TrendingUp, title: 'زيادة المبيعات', desc: 'ارفع نسبة الحجوزات والمشتريات' },
          { icon: Eye, title: 'ظهور موسع', desc: 'اعرض إعلانك في الصفحة الرئيسية والبحث' },
          { icon: Hotel, title: 'فنادق ومنتجعات', desc: 'روّج لعروض الإقامة والعطلات' },
          { icon: Utensils, title: 'مطاعم ومقاهي', desc: 'اجذب الزبائن بقوائمك وخصوماتك' },
          { icon: Plane, title: 'وكالات سفر', desc: 'اعرض باقات السفر والرحلات المحلية' },
        ]}
      />

      {/* Interactive Ad Form Demo */}
      <section className="py-12 md:py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-bold mb-2">كيف تنشئ إعلانك؟</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto text-sm">
              جرب نموذج الإعلان التفاعلي – حدد جمهورك المستهدف بسهولة
            </p>
          </div>
          <Card className="max-w-3xl mx-auto">
            <CardHeader>
              <CardTitle>نموذج إعلان تجريبي</CardTitle>
              <CardDescription>املأ البيانات التالية لترى كيف سيبدو إعلانك (للتوضيح فقط)</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Ad Title */}
              <div>
                <Label>عنوان الإعلان</Label>
                <Input
                  placeholder="مثال: عروض خاصة على الأجهزة الإلكترونية"
                  value={adTitle}
                  onChange={(e) => setAdTitle(e.target.value)}
                  className="mt-1"
                />
              </div>

              {/* Image Upload Preview */}
              <div>
                <Label>صورة الإعلان</Label>
                <div className="mt-1 flex items-center justify-center w-full">
                  <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer bg-muted/30 hover:bg-muted/50 transition">
                    <div className="flex flex-col items-center justify-center pt-5 pb-6">
                      <Upload className="w-8 h-8 mb-2 text-muted-foreground" />
                      <p className="text-sm text-muted-foreground">اضغط لرفع صورة (PNG, JPG)</p>
                    </div>
                    <input type="file" className="hidden" />
                  </label>
                </div>
              </div>

              {/* Age Range Slider */}
              <div>
                <Label>الفئة العمرية المستهدفة: {ageRange[0]} – {ageRange[1]} سنة</Label>
                <Slider
                  value={ageRange}
                  onValueChange={setAgeRange}
                  min={13}
                  max={70}
                  step={1}
                  className="mt-2"
                />
              </div>

              {/* Cities Multi-select */}
              <div>
                <Label>المدن المستهدفة</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {citiesList.map(city => (
                    <div key={city} className="flex items-center space-x-2 space-x-reverse">
                      <Checkbox
                        id={`city-${city}`}
                        checked={selectedCities.includes(city)}
                        onCheckedChange={() => handleCityToggle(city)}
                      />
                      <Label htmlFor={`city-${city}`} className="text-sm font-normal">{city}</Label>
                    </div>
                  ))}
                </div>
              </div>

              {/* Neighborhoods Multi-select (conditionally shown if city selected) */}
              {selectedCities.includes('العيون') && (
                <div>
                  <Label>أحياء العيون المستهدفة</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {neighborhoodsList.map(hood => (
                      <div key={hood} className="flex items-center space-x-2 space-x-reverse">
                        <Checkbox
                          id={`hood-${hood}`}
                          checked={selectedNeighborhoods.includes(hood)}
                          onCheckedChange={() => handleNeighborhoodToggle(hood)}
                        />
                        <Label htmlFor={`hood-${hood}`} className="text-sm font-normal">{hood}</Label>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Interests */}
              <div>
                <Label>الاهتمامات</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {['إلكترونيات', 'أزياء', 'سياحة', 'مطاعم', 'أثاث', 'رياضة'].map(interest => (
                    <div key={interest} className="flex items-center space-x-2 space-x-reverse">
                      <Checkbox
                        id={`interest-${interest}`}
                        checked={interests.includes(interest)}
                        onCheckedChange={() => handleInterestToggle(interest)}
                      />
                      <Label htmlFor={`interest-${interest}`} className="text-sm font-normal">{interest}</Label>
                    </div>
                  ))}
                </div>
              </div>

              {/* Gender Radio */}
              <div>
                <Label>الجنس</Label>
                <RadioGroup value={gender} onValueChange={setGender} className="flex gap-4 mt-2">
                  <div className="flex items-center space-x-2 space-x-reverse">
                    <RadioGroupItem value="all" id="gender-all" />
                    <Label htmlFor="gender-all">الجميع</Label>
                  </div>
                  <div className="flex items-center space-x-2 space-x-reverse">
                    <RadioGroupItem value="male" id="gender-male" />
                    <Label htmlFor="gender-male">رجال</Label>
                  </div>
                  <div className="flex items-center space-x-2 space-x-reverse">
                    <RadioGroupItem value="female" id="gender-female" />
                    <Label htmlFor="gender-female">نساء</Label>
                  </div>
                </RadioGroup>
              </div>

              <Button className="w-full" onClick={handlePostAd}>
                نشر الإعلان (تجريبي)
              </Button>
              <p className="text-xs text-muted-foreground text-center">
                هذا مجرد نموذج توضيحي. عند النشر سيتم توجيهك إلى لوحة التحكم لإنشاء الإعلان الفعلي.
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      <LocalPresenceSection
        title="وصول إلى العملاء في مدينتك"
        subtitle="يمكنك اختيار مناطق محددة في العيون، السمارة، بوجدور، والطرفاية. إعلانك سيظهر فقط للزبائن في الأماكن التي تختارها."
        cities={citiesList}
        neighborhoods={neighborhoodsList}
        imageSrc={featuresImg}
      />

      <CTABanner
        title="استعد لزيادة مبيعاتك وحجوزاتك"
        description="انضم إلى مئات المنشآت التي تعلن يومياً عبر سكيكيمة – ابدأ حملتك الإعلانية الآن."
        buttonText="أطلق إعلانك – ابدأ من 25 درهماً"
        onButtonClick={handlePostAd}
        imageSrc={ctaImg}
      />
    </div>
  )
}